import { useState, useEffect, useCallback, useRef } from "react";
import { getBookmarkedItems } from "../api";
import { RecommendationItem } from "../../RecommendationFeed/api";
import * as Sentry from "@sentry/react";
import { useRecommendationFeedStore } from "../../../stores/recommendationFeedStore";
import { UserActivityType } from "../../RecommendationFeed/types/activity_types";

interface UseBookmarksQueryResult {
  data: RecommendationItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  removeBookmark: (resourceId: string) => void;
  itemsBeingRemoved: Set<string>; // Track items currently being animated out
}

export const useBookmarksQuery = (): UseBookmarksQueryResult => {
  // Initialize state
  const [data, setData] = useState<RecommendationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsBeingRemoved, setItemsBeingRemoved] = useState<Set<string>>(
    new Set()
  );
  const pageSize = 10;

  // Track loaded item IDs to prevent duplicates
  const loadedItemIds = useRef<Set<string>>(new Set());

  // Track initialization status
  const isInitialized = useRef(false);

  // Reset state when component is mounted or remounted
  useEffect(() => {
    setIsLoading(true);
    setPage(1);
    loadedItemIds.current = new Set();
    isInitialized.current = false;
    return () => {
      isInitialized.current = false;
    };
  }, []);

  // Use useCallback to prevent recreation on every render
  const loadMore = useCallback(async () => {
    try {
      // Don't load if we're already loading
      if (isLoading || isLoadingMore) {
        return;
      }

      // Store the current page number to use throughout this function execution
      const currentPage = page;

      if (!isInitialized.current) {
        // This is our first load
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      // Pass the current page number to the API call
      const response = await getBookmarkedItems(currentPage, pageSize);

      if (response.error) {
        throw new Error(response.error);
      }

      // Handle API response format
      const items = response.items || [];

      if (items.length > 0) {
        // Filter out any duplicate items before updating the data
        const uniqueItems = items.filter(
          (item) => !loadedItemIds.current.has(item.id)
        );

        // Add new item IDs to our tracking set
        uniqueItems.forEach((item) => loadedItemIds.current.add(item.id));

        // Mark all items as bookmarked in the store
        const { addUserActivity } = useRecommendationFeedStore.getState();
        uniqueItems.forEach((item) => {
          addUserActivity({
            resource_id: item.id,
            resource_type: item.type,
            activity_type: UserActivityType.BOOKMARKED,
          });
        });

        // For first page, replace data. For subsequent pages, append unique items only
        setData((prev) => {
          const newData = !isInitialized.current
            ? uniqueItems
            : [...prev, ...uniqueItems];
          return newData;
        });

        // Mark as initialized after successful data load
        if (!isInitialized.current) {
          isInitialized.current = true;
        }

        // Check if there are more items to load
        const hasMorePages = currentPage < (response.total_pages || 1);
        setHasMore(hasMorePages);

        // Only increment page number if we've successfully loaded the current page
        // This ensures the next loadMore call will request the next page
        setPage(currentPage + 1);
      } else {
        // No items returned
        // Only clear data if we're on page 1 AND this isn't a subsequent request after having data
        if (currentPage === 1 && data.length === 0) {
          setData([]);
        }
        setHasMore(false);
      }
      setError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load bookmarks";
      setError(errorMessage);

      Sentry.captureException(err, {
        tags: {
          component: "useBookmarksQuery",
        },
        extra: {
          pageSize,
          isInitialized: isInitialized.current,
          loadedItemCount: loadedItemIds.current.size,
        },
      });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [page, pageSize, data, isLoading, isLoadingMore, hasMore, loadedItemIds]);

  // Load data when component mounts or when initialization is needed
  useEffect(() => {
    if (!isInitialized.current && !isLoading) {
      loadMore();
    }
  }, [isLoading, loadMore]);

  // Function to remove a bookmark from the feed with animation
  const removeBookmark = useCallback((resourceId: string) => {
    // Mark the item as being removed (for animation)
    setItemsBeingRemoved((prev) => {
      const newSet = new Set(prev);
      newSet.add(resourceId);
      return newSet;
    });

    // After animation completes, actually remove the item
    setTimeout(() => {
      // Remove from the local data
      setData((prev) => prev.filter((item) => item.id !== resourceId));

      // Remove from the set of items being removed
      setItemsBeingRemoved((prev) => {
        const newSet = new Set(prev);
        newSet.delete(resourceId);
        return newSet;
      });

      // Remove from the loaded items set
      loadedItemIds.current.delete(resourceId);
    }, 500); // Match this with the CSS animation duration
  }, []);

  return {
    data,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    removeBookmark,
    itemsBeingRemoved,
  };
};
